import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Link,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DropzoneProps } from "~/components/dropzone";
import {
  ChevronRight as ChevronRightIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { Command } from "~/components/guides/components";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  ClientIntegration,
  ClientIntegrationType,
  GetClientIntegrationDocument,
  GetIntegrationsSummaryDocument,
  Ms365ConfigurationOptionsInput,
  useCreateClientIntegrationMutation,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { useSnackbar } from "notistack";
import { AnimatePresence } from "motion/react";
import { useNavigate } from "react-router-dom";
import { getError } from "~/lib/handle-error";
import { UpdateFlowData } from "~/pages/integrations/utils/integrationsTypes";
import {
  GUID_PATTERN,
  ID_PLACEHOLDER,
  MATCHING_ID_ERROR_MESSAGE,
  ValidationMessage,
} from "~/pages/integrations/utils/integrationValidationUtils";
import { helperTextStyles } from "~/pages/security-model/SlaConfiguration";
import { setDocumentTitle } from "~/lib/commonUtils";
import { HostedIntegrationDropzoneSuccess } from "../../components/HostedIntegrationDropzoneSuccess";
import { HostedIntegrationDropzoneUpload } from "../../components/HostedIntegrationDropzoneUpload";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

type CloudFormInput = {
  integrationName: string;
  tenantId: string;
  clientId: string;
  pemKey: string | null;
};

const defaultValues: CloudFormInput = {
  integrationName: "",
  tenantId: "",
  clientId: "",
  pemKey: null,
};

interface Ms365ManualFormProps {
  defaultIntegrationName: string;
  scope: SpaceScope | OrganizationScope;
  setView: React.Dispatch<React.SetStateAction<"setup" | "add policies">>;
  updateFlow: UpdateFlowData | undefined;
  setCreatedIntegrationMrn: React.Dispatch<
    React.SetStateAction<ClientIntegration["mrn"] | undefined>
  >;
}

export function Ms365ManualForm({
  scope,
  updateFlow,
  defaultIntegrationName,
  setView,
  setCreatedIntegrationMrn,
}: Ms365ManualFormProps) {
  let navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [refreshKey, setRefreshKey] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    getFieldState,
    formState: { errors, isValid, isSubmitSuccessful },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      ...defaultValues,
      integrationName: defaultIntegrationName,
    },
  });

  const [tenantId, clientId, pemKey] = watch([
    "tenantId",
    "clientId",
    "pemKey",
  ]);

  const [createIntegration] = useCreateClientIntegrationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      if (data.createClientIntegration.integration) {
        setCreatedIntegrationMrn(data.createClientIntegration.integration.mrn);
      }
    },
    refetchQueries: [
      {
        query: GetIntegrationsSummaryDocument,
        variables: { input: { spaceMrn: scope.mrn } },
      },
    ],
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  useEffect(() => {
    if (updateFlow) {
      if (
        updateFlow.integration.configurationOptions?.__typename !==
        "Ms365ConfigurationOptions"
      )
        return;
      reset({
        integrationName: updateFlow.integration.name,
        tenantId: updateFlow.integration.configurationOptions?.tenantId,
        clientId: updateFlow.integration.configurationOptions?.clientId,
      });
    }
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful && !updateFlow) {
      reset(defaultValues);
    }
  }, [isSubmitSuccessful]);

  useEffect(() => {
    trigger("clientId");
  }, [tenantId]);

  useEffect(() => {
    trigger("tenantId");
  }, [clientId]);

  const onSubmit: SubmitHandler<CloudFormInput> = async (data) => {
    if (!updateFlow && !data.pemKey) {
      throw new Error("You must supply a valid .pem certificate");
    }

    const ms365ConfigurationOptions: Ms365ConfigurationOptionsInput = {
      tenantId: data.tenantId,
      clientId: data.clientId,
      certificate: data.pemKey ? data.pemKey : null,
    };

    try {
      if (updateFlow) {
        await updateIntegration({
          variables: {
            input: {
              name: data.integrationName.trim(),
              mrn: getIntegrationMrn({
                scope,
                integrationId:
                  updateFlow?.integration.mrn.split("/").pop() || "",
              }),
              type: ClientIntegrationType.Ms365,
              configurationOptions: {
                ms365ConfigurationOptions,
              },
            },
          },
        });
        const integrationId = updateFlow.integration.mrn.split("/").pop();
        enqueueSnackbar("Successfully updated configuration", {
          variant: "success",
        });
        navigate(`/space/integrations/ms365/${integrationId}/?${scope.params}`);
      } else {
        await createIntegration({
          variables: {
            input: {
              spaceMrn: scope.mrn,
              name: data.integrationName,
              type: ClientIntegrationType.Ms365,
              longLivedToken: false,
              configurationOptions: {
                ms365ConfigurationOptions,
              },
            },
          },
        });
        setView("add policies");
      }
    } catch (e) {
      const msg = getError(e);
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const onDropAccepted: DropzoneProps["onDropAccepted"] = async (files) => {
    setValue("pemKey", await files[0].text());
    setRefreshKey(false);
  };

  const handleDropzoneRefresh = () => {
    setValue("pemKey", "");
    setRefreshKey(true);
  };

  setDocumentTitle(["M365", "Integrations Setup"]);

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Step 1 */}
        <Box pb={4}>
          <Command
            number={1}
            options={{
              fontSize: { xs: 16 },
              dotColor: theme.palette.background.lightest,
            }}
          >
            Create your Microsoft app registration
          </Command>

          <Box pl={6}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, mt: 2 }}
            >
              Before you can integrate your Microsoft account with any third
              party service,must create an app registration with the proper
              permissions. You can do this in the Microsoft Entra admin center.
              To learn more, read the{" "}
              <Link
                href="https://learn.microsoft.com/en-us/security/zero-trust/develop/app-registration"
                target="_blank"
                rel="noopener"
              >
                Microsoft documentation
              </Link>
              .
            </Typography>

            <Button
              href="https://aad.portal.azure.com"
              target="_blank"
              variant="outlined"
              color="secondary"
              endIcon={<OpenInNewIcon />}
            >
              Go to Entra admin center
            </Button>
          </Box>
        </Box>
        {/* Step 2 */}
        <Box pb={4}>
          <Command
            number={2}
            options={{
              fontSize: { xs: 16 },
              dotColor: theme.palette.background.lightest,
            }}
          >
            Choose an integration name
          </Command>

          <Box pl={6}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, mt: 2 }}
            >
              Please choose a descriptive name that lets you easily identify
              your integration.
            </Typography>
            <Controller
              name="integrationName"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  sx={{
                    background: theme.palette.code.background,
                    borderRadius: 1,
                    color: "text.primary",
                    ...helperTextStyles,
                  }}
                  placeholder="Your integration name..."
                  error={Boolean(errors.integrationName)}
                  helperText={
                    Boolean(errors.integrationName) && (
                      <ValidationMessage error={errors.integrationName} />
                    )
                  }
                />
              )}
            />
          </Box>
        </Box>

        {/* step 3 */}
        <Box pb={4}>
          <Command
            number={3}
            options={{
              fontSize: { xs: 16 },
              dotColor: theme.palette.background.lightest,
            }}
          >
            Enter the Tenant (Directory) ID
          </Command>
          <Box pl={6}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, mt: 2 }}
            >
              Find it on the{" "}
              <Link
                href="https://entra.portal.azure.com"
                target="_blank"
                rel="noopener"
              >
                Microsoft Entra admin center
              </Link>
              's Tenant page: On the Overview tab, it's under Basic information.
            </Typography>
            <Controller
              name="tenantId"
              control={control}
              rules={{
                required: true,
                pattern: GUID_PATTERN,
                validate: {
                  matchingIds: (value: CloudFormInput["tenantId"]) =>
                    value !== clientId || MATCHING_ID_ERROR_MESSAGE,
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  sx={{
                    background: theme.palette.code.background,
                    borderRadius: 1,
                    color: "text.primary",
                    ...helperTextStyles,
                  }}
                  placeholder={ID_PLACEHOLDER}
                  error={
                    getFieldState("tenantId").isTouched &&
                    Boolean(errors.tenantId)
                  }
                  helperText={
                    Boolean(errors.tenantId) &&
                    getFieldState("tenantId").isTouched && (
                      <ValidationMessage error={errors.tenantId} />
                    )
                  }
                />
              )}
            />
          </Box>
        </Box>
        {/* step 4 */}
        <Box pb={4}>
          <Command
            number={4}
            options={{
              fontSize: { xs: 16 },
              dotColor: theme.palette.background.lightest,
            }}
          >
            Enter the application (client) ID
          </Command>
          <Box pl={6}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, mt: 2 }}
            >
              Find it in the Application (client) ID box on the “Overview” page
              of your app registration. To learn more, read the{" "}
              <Link
                href="https://learn.microsoft.com/en-us/azure/healthcare-apis/register-application?wt.mc_id=searchAPI_azureportal_inproduct_rmskilling&sessionId=43aa24014475423fb9fdd085ba970015#application-id-client-id"
                target="_blank"
                rel="noopener"
              >
                Microsoft documentation
              </Link>
              .
            </Typography>
            <Controller
              name="clientId"
              control={control}
              rules={{
                required: true,
                pattern: GUID_PATTERN,
                validate: {
                  matchingIds: (value: CloudFormInput["clientId"]) =>
                    value !== tenantId || MATCHING_ID_ERROR_MESSAGE,
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  sx={{
                    background: theme.palette.code.background,
                    borderRadius: 1,
                    color: "text.primary",
                    ...helperTextStyles,
                  }}
                  placeholder={ID_PLACEHOLDER}
                  error={
                    getFieldState("clientId").isTouched &&
                    Boolean(errors.clientId)
                  }
                  helperText={
                    Boolean(errors.clientId) &&
                    getFieldState("clientId").isTouched && (
                      <ValidationMessage error={errors.clientId} />
                    )
                  }
                />
              )}
            />
          </Box>
        </Box>

        {/* step 5 */}
        <Box pb={4}>
          <Command
            number={5}
            options={{
              fontSize: { xs: 16 },
              dotColor: theme.palette.background.lightest,
            }}
          >
            Provide your certificate and private key
          </Command>
          <Box pl={6}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, mt: 2 }}
            >
              Upload a valid PEM file containing your certificate and private
              key. To learn more, read the{" "}
              <Link
                href="https://mondoo.com/docs/infra/saas/overview"
                target="_blank"
                rel="noopener"
              >
                Mondoo documentation
              </Link>
              .
            </Typography>
            <AnimatePresence mode="wait">
              {/* if we're in creation flow and don't have a pemKey */}
              {/* if we're in updateFlow, and user has requested to refreshKey */}
              {((!pemKey && !updateFlow) || (refreshKey && updateFlow)) && (
                <HostedIntegrationDropzoneUpload
                  key="upload"
                  onDropAccepted={onDropAccepted}
                />
              )}

              {/* if we're in creation flow and already have a pemKey */}
              {/* if we're in the update flow and have not requested a refresh OR have already refreshed */}
              {((pemKey && !updateFlow) || (!refreshKey && updateFlow)) && (
                <HostedIntegrationDropzoneSuccess
                  key="success"
                  {...{
                    handleDropzoneRefresh,
                    isUpdateFlow: Boolean(updateFlow),
                  }}
                />
              )}
            </AnimatePresence>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "end" }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            endIcon={<ChevronRightIcon />}
            // setValue doesn't trigger the required validation so we're doing it manually here
            disabled={updateFlow ? !isValid : !isValid || !pemKey}
          >
            {updateFlow ? "update configuration" : "start scanning"}
          </Button>
        </Box>
      </form>
    </Box>
  );
}
